import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {Router} from '@angular/router';
import {Subscription, timer} from "rxjs";
import {finalize} from 'rxjs/operators';

import {Dialect, DialectMapPayload, SimpleDialect} from '../../models/dialect.model';
import {DialectService} from '../../services/dialect/dialect.service';
import {DialectBookmarkService} from '../../services/dialect/dialect-bookmark.service';
import {ScrollDownService} from "../../services/scroll-down/scroll-down.service";
import {DEFAULT_SCROLL_SPEED} from "../../defines/client.const";
import {DialectDetailStandardComponent} from "./dialect-detail-standard.component";
import {multiLinkMixin} from "../../mixins/multi-link.mixin";
import {DynamicComponentService} from "../../services/dynamic-component/dynamic-component.service";
import {GlobalEventService} from "../../services/global-event/global-event.service";
import {LayoutMgrService} from "../../services/layout-mgr/layout-mgr.service";


@Component({
  selector: 'app-dialect-detail',
  templateUrl: './dialect-detail.component.html',
  styleUrls: ['./dialect-detail.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush, // 책갈피 때문에 넣으면 안된다.
})
export class DialectDetailComponent extends multiLinkMixin implements OnInit, OnDestroy {
  @ViewChild(DialectDetailStandardComponent) standardCom: DialectDetailStandardComponent;
  @ViewChild('menuWrap') menuWrap: ElementRef;

  @Input() dialect_id: string;
  @Input() height: number;
  @Output() closeDetail: EventEmitter<boolean>;
  @Output() openMap: EventEmitter<DialectMapPayload>;

  dialect: Dialect;
  wordMoreItems: Array<string> = [];
  sameAreaDialects: Array<SimpleDialect> = [];
  otherAreaDialects: Array<SimpleDialect> = [];
  private sendingBookmark = false;

  // 푸터의 아래로 내리기 버튼 구독
  private scrollDown$: Subscription | null = null;

  private _classLong = false;
  public get classLong(): boolean {
    return this._classLong;
  }

  constructor(
    public router: Router,
    public dynamicSvc: DynamicComponentService,
    public lm: LayoutMgrService,
    private cdRef: ChangeDetectorRef,
    private elRef: ElementRef,
    private dialectSvc: DialectService,
    private bmSvc: DialectBookmarkService,
    private scrollDownSvc: ScrollDownService,
    private globalEventSvc: GlobalEventService,
  ) {
    super(router, dynamicSvc, lm);
    this.closeDetail = new EventEmitter<boolean>();
    this.openMap = new EventEmitter<DialectMapPayload>();
  }

  ngOnInit(): void {
    this.dialectSvc.GetDetail(this.dialect_id)
      .subscribe(data => {
        console.log('DialectDetailComponent::dialectSvc.GetDetail() =', data);
        this.setDialect(data);

        this.cdRef.detectChanges();

        // 표준말이 너무 길어서 줄을 바꿔야 하는지 확인한다.
        timer(100).subscribe(() => {
          this._classLong = this.elRef.nativeElement?.clientWidth < this.standardCom?.width + this.menuWrap?.nativeElement?.clientWidth;
          // console.warn("DialectDetailComponent::ngOnInit() dialectSvc.GetDetail() return", this._classLong, this.elRef.nativeElement?.clientWidth, this.standardCom?.width, this.menuWrap?.nativeElement.clientWidth);
        });
      }, err => console.error(err));

    this.scrollDown$ = this.scrollDownSvc.pushScrollDown$().subscribe(isFirst => {
      const scrollDelta = DEFAULT_SCROLL_SPEED * (isFirst ? 2 : 1);  // 스크롤 시작할 때 첫번째는 두배로 스크롤 함.

      this.elRef?.nativeElement.scrollTo({
        top: this.elRef.nativeElement.scrollTop + scrollDelta,
        behavior: 'auto',
      });
    });
  }


  ngOnDestroy() {
    this.scrollDownSvc.popScrollDown$();
    if (this.scrollDown$) {
      this.scrollDown$.unsubscribe();
      this.scrollDown$ = null;
    }
  }

  public onClose(): void {
    const bookmark = this.dialect?.bookmark ?? false;
    console.log('onClose()', bookmark);
    this.closeDetail.emit(bookmark);
  }

  public ToggleBookmark(event: MouseEvent, dialect: Dialect): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.sendingBookmark) {
      return;
    }
    this.sendingBookmark = true;
    dialect.bookmark = !dialect.bookmark;
    this.bmSvc.sendBookmark(dialect.id, dialect.bookmark).pipe(
      finalize(() => this.sendingBookmark = false)
    ).subscribe(
      data => console.log(data),
      err => console.error(err)
    );
  }

  public getBmCss(dialect: Dialect): string {
    if (this.sendingBookmark) {
      return 'ing';
    }
    return dialect.bookmark ? 'on' : '';
  }

  public onOpenMap(): void {
    this.openMap.emit({
      word: this.dialect.standard,
      dialects: this.otherAreaDialects,
    });
  }

  public onClickSameAreaDialect(event: MouseEvent | KeyboardEvent, dialect: SimpleDialect): void {
    event.preventDefault();
    event.stopPropagation();

    if (event instanceof MouseEvent) {
      this.globalEventSvc.Click(event, true);
    }

    const ids = ';' + dialect.id;
    this.OpenMultiLink(ids).subscribe({
      next: resultLinkPopup => {
        console.warn('DialectDetailComponent.onClickSameAreaDialect() resultLinkPopup =', resultLinkPopup);
      },
      error: err => {
        console.error('DialectDetailComponent.onClickSameAreaDialect() error =', err);
      },
    });
  }

  public getWordMoreDisplay(wordMore: string, isLast: boolean): string {
    return wordMore + (!isLast ? ',\u00A0' : '');
  }

  private setDialect(dialect: Dialect): void {
    this.dialect = dialect;

    const dialects = dialect.dialects ?? [];
    this.sameAreaDialects = dialects.filter(other => other.area1 === dialect.area1 && other.id !== dialect.id);
    this.otherAreaDialects = dialects.filter(other => other.area1 !== dialect.area1);

    const sameAreaWords = new Set(
      this.sameAreaDialects
        .map(other => other.word?.trim())
        .filter((word): word is string => !!word)
    );
    this.wordMoreItems = this.getWordMoreItems(dialect.wordMore)
      .filter(word => !sameAreaWords.has(word.trim()));
  }

  private getWordMoreItems(wordMore: string | null): Array<string> {
    return (wordMore ?? '')
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0);
  }
}
